import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import {
	SCORE_RANGE_COLORS,
	SCORE_RANGE_COLOR_ORDER,
	SCORE_RANGE_LABEL_COLORS,
	SCORE_RANGE_LIGHT_LABEL_COLORS,
	ScoreRangeColor
} from 'src/app/shared/sct-charts/score-range-colors';

type BatteryTypeFilter = 'all' | 'lead_acid' | 'li_ion';

// One battery-type group of GET site/get-battery-fleet-quick-view: device counts per bar.
interface FleetQuickViewGroup {
	total: number;
	soc: number[];
	plugs: number[];
}

type FleetQuickViewData = Record<BatteryTypeFilter, FleetQuickViewGroup>;

interface QuickViewBar {
	label: string;
	color: ScoreRangeColor;
}

// Same rule as the score bar charts (ST-2645): Google Charts moves a label outside a bar too short to
// hold it, where a white label would vanish - so only bars at least this wide get a white label.
const MIN_PERCENT_FOR_LIGHT_LABEL = 10;

// Plug-ins today: 0 red, 1 orange, 2 yellow, 3 and 4 light green, 5+ dark green.
const PLUG_IN_BAR_COLORS: ScoreRangeColor[] = ['red', 'orange', 'yellow', 'lightGreen', 'lightGreen', 'darkGreen'];

@Component({
	selector: 'app-battery-fleet-quick-view',
	templateUrl: './battery-fleet-quick-view.component.html',
	styleUrls: ['./battery-fleet-quick-view.component.css']
})
export class BatteryFleetQuickViewComponent implements OnInit, OnDestroy {
	siteSub: Subscription = new Subscription();
	siteId = 0;

	data: FleetQuickViewData | null = null;
	dataLoaded = false;
	noData = false;

	batteryTypeOptions = [
		{ value: 'all', label: this.translateService.instant('g.all') },
		{ value: 'lead_acid', label: this.translateService.instant('fleet_reports.lead_acid') },
		{ value: 'li_ion', label: this.translateService.instant('fleet_reports.li_ion') },
	];
	selectedBatteryType: BatteryTypeFilter = 'all';

	// SOC: ten 10% bars, two per color - the score bar charts' scheme (ST-2645).
	socBars: QuickViewBar[] = Array.from({ length: 10 }, (_, i) => ({
		label: `${i ? i * 10 + 1 : 0}–${(i + 1) * 10}%`,
		color: SCORE_RANGE_COLOR_ORDER[Math.floor(i / 2)]
	}));

	plugInBars: QuickViewBar[] = PLUG_IN_BAR_COLORS.map((color, i) => ({
		label: i == PLUG_IN_BAR_COLORS.length - 1 ? `${i}+` : `${i}`,
		color
	}));

	socChartData = [];
	plugInChartData = [];

	// Two stacked series that look the same: a bar is drawn by series 0 when its label is dark and by
	// series 1 when it is white, since Google Charts can only set the label color per series.
	columnNames = [
		this.translateService.instant('fleet_reports.range'),
		this.translateService.instant('fleet_reports.percent_of_total_batteries'),
		{ role: 'style' },
		{ role: 'annotation' },
		{ role: 'tooltip', type: 'string', p: { html: true } },
		this.translateService.instant('fleet_reports.percent_of_total_batteries'),
		{ role: 'style' },
		{ role: 'annotation' },
		{ role: 'tooltip', type: 'string', p: { html: true } }
	];

	socChartOptions = this.buildChartOptions('fleet_reports.soc_range');
	plugInChartOptions = this.buildChartOptions('fleet_reports.plug_ins_today');

	constructor(
		private route: ActivatedRoute,
		private translateService: TranslateService,
		private commonService: CommonService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
	) { }

	ngOnInit() {
		this.siteId = +this.route.parent?.snapshot.params.siteId || 0;

		if (!this.siteId)
			return;

		this.siteSub = this.sideMenuService.currentSite.subscribe((site) => {
			if (!site || !site['id'])
				return;

			this.commonService.setPageTabTitle(`${site['customer_name']} - ${site['name']} - ${this.translateService.instant('fleet_reports.battery_fleet_quick_view')}`);
		});

		this.getBatteryFleetQuickView();
	}

	getBatteryFleetQuickView() {
		this.siteDashboardService.getBatteryFleetQuickView(this.siteId).subscribe((data: FleetQuickViewData) => {
			this.data = data;
			this.dataLoaded = true;
			this.updateCharts();
		});
	}

	updateCharts() {
		const group = this.data?.[this.selectedBatteryType];

		this.noData = !group?.total;
		if (this.noData) {
			this.socChartData = [];
			this.plugInChartData = [];
			return;
		}

		this.socChartData = this.buildChartData(group.soc, this.socBars, group.total);
		this.plugInChartData = this.buildChartData(group.plugs, this.plugInBars, group.total);
	}

	buildChartData(counts: number[], bars: QuickViewBar[], total: number) {
		const countLabel = this.translateService.instant('site_performance.count_of_batteries');
		const totalLabel = this.translateService.instant('fleet_reports.total_count_of_batteries');

		return bars.map((bar, i) => {
			const count = counts?.[i] || 0;
			const percent = total ? Math.round((count / total) * 100) : 0;
			const tooltip = `
				<div style="padding:8px; white-space: nowrap;">
					<div><strong>${countLabel}: ${count}</strong></div>
					<div><strong>${totalLabel}: ${total}</strong></div>
				</div>
			`;

			const style = `color: ${SCORE_RANGE_COLORS[bar.color]}`;
			const isLightLabel = percent >= MIN_PERCENT_FOR_LIGHT_LABEL && SCORE_RANGE_LIGHT_LABEL_COLORS.includes(bar.color);

			// The series not drawing this bar gets 0 and an empty label rather than null: sct-charts turns
			// null into 0, and a "0" label would then be drawn next to the bar.
			return [
				bar.label,
				isLightLabel ? 0 : percent, style, isLightLabel ? '' : `${percent}`, tooltip,
				isLightLabel ? percent : 0, style, isLightLabel ? `${percent}` : '', tooltip
			];
		});
	}

	buildChartOptions(vAxisTitleKey: string) {
		return {
			legend: { position: 'none' },
			hAxis: {
				title: this.translateService.instant('fleet_reports.percent_of_total_batteries'),
				minValue: 0,
				maxValue: 100,
				format: '#\'%\''
			},
			vAxis: {
				title: this.translateService.instant(vAxisTitleKey)
			},
			tooltip: {
				isHtml: true
			},
			annotations: {
				alwaysOutside: false,
				highContrast: false,
				textStyle: {
					fontSize: 12,
					color: SCORE_RANGE_LABEL_COLORS.dark
				}
			},
			bar: { groupWidth: '70%' },
			isStacked: true,
			// no white outline ("aura") behind the labels - it blurs white text
			series: {
				0: { annotations: { textStyle: { fontSize: 12, color: SCORE_RANGE_LABEL_COLORS.dark, auraColor: 'none' } } },
				1: { annotations: { textStyle: { fontSize: 12, color: SCORE_RANGE_LABEL_COLORS.light, auraColor: 'none' } } }
			},
			chartArea: {
				left: 100,
				right: 30,
				top: 40,
				bottom: 50
			}
		};
	}

	ngOnDestroy() {
		this.siteSub.unsubscribe();
	}
}
